import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { form, FormField, maxLength, minLength, pattern, required } from '@angular/forms/signals';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectItemInterface, NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import {
  DeviceTypes,
  MedServiceDto,
  UpdateMedServiceWebRequest,
} from '../../../../core/api/mhsConfigurationApiClient';
import { ModalityLookup } from '../../../../core/context/modalities-lookup';
import { ResourceService } from '../../../../core/services/resource-service';
import { FieldErrorsComponent } from '../../../../shared/ui/components/field-error/field-error';
import { IProcedureCodePickerData } from '../../dto/procedure-code-picker-data';
import { ProcedureData } from '../../dto/procedure-data';
import { ProceduresService } from '../../services/procedures-service';
import { ProcedureCodePicker } from '../procedure-code-picker/procedure-code-picker';

@Component({
  selector: 'app-procedure-edit',
  imports: [
    ReactiveFormsModule,
    FormField,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzModalModule,
    NzCheckboxModule,
    RouterLink,
    NzFlexModule,
    NzGridModule,
    NzDividerModule,
    NzIconModule,
    NzTagModule,
    CommonModule,
    NzAvatarModule,
    NzDropdownModule,
    NzSpinModule,
    FieldErrorsComponent,
  ],
  templateUrl: './procedure-edit.html',
  styleUrl: './procedure-edit.scss',
})
export class ProcedureEditComponent {
  readonly procedureService = inject(ProceduresService);
  readonly resourceService = inject(ResourceService);
  readonly modalService = inject(NzModalService);
  readonly router = inject(Router);
  readonly messageService = inject(NzMessageService);
  readonly viewContainerRef = inject(ViewContainerRef);
  readonly route = inject(ActivatedRoute);
  readonly procedureId = Number(this.route.snapshot.paramMap.get('id'));
  readonly isLoaded = signal(false);
  readonly isSubmitting = signal(false);
  readonly submitted = signal(false);

  readonly bodyAreas = toSignal(this.resourceService.getBodyAreas());
  readonly sortedBodyAreas = computed(() =>
    [...(this.bodyAreas() ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pl')),
  );
  readonly procedureModel = signal<ProcedureData>({
    type: 'CT',
    name: '',
    icd9Code: '',
    code: '',
    bodyAreaCode: '',
    nhfPoints: '',
    withContrast: '',
    withSpine: '',
    withPrePostContrast: '',
    defaultEtcInMinutes: '',
  });
  readonly procedureForm = form(this.procedureModel, (procedureModel) => {
    required(procedureModel.name);
    required(procedureModel.type);
    required(procedureModel.icd9Code);
    minLength(procedureModel.name, 5, {
      message: 'Nazwa procedury powinna mieć co najmniej 5 znaków',
    });
    maxLength(procedureModel.name, 512, {
      message: 'Nazwa procedury powinna mieć co najwyżej 512 znaków',
    });
    minLength(procedureModel.icd9Code, 1, {
      message: 'Kod ICD-9 powinien mieć co najmniej 1 znak',
    });
    maxLength(procedureModel.icd9Code, 16, {
      message: 'Kod ICD-9 powinien mieć co najwyżej 16 znaków',
    });
    pattern(procedureModel.code, /^\d\.\d{2}\.\d{2}\.\d{7}$/, {
      message: 'Kod procedury powinien mieć format X.XX.XX.XXXXXXX',
    });
  });
  readonly modalities = ModalityLookup.modalities;

  constructor() {
    this.loadData();
  }

  private loadData(): void {
    this.procedureService.get(this.procedureId).subscribe({
      next: (procedure) => {
        this.procedureModel.set(this.mapFromApiModel(procedure));
        this.isLoaded.set(true);
      },
      error: (error) => {
        this.messageService.error('Wystąpił błąd podczas pobierania danych procedury');
        console.error(error);
      },
    });
  }

  showIcdCodePicker(codeType: string): void {
    this.modalService.create<ProcedureCodePicker, IProcedureCodePickerData>({
      nzTitle: `Wyszukaj kod ${codeType}`,
      nzContent: ProcedureCodePicker,
      nzViewContainerRef: this.viewContainerRef,
      nzData: {
        codeType: codeType,
      },
      nzOnOk: (instance) => {
        this.procedureModel.update((model) => ({
          ...model,
          icd9Code: instance.selectedCode ?? '',
        }));
      },
    });
  }

  mapToApiModel(model: ProcedureData): UpdateMedServiceWebRequest {
    const request = new UpdateMedServiceWebRequest();

    request.id = this.procedureId;
    request.type = this.mapToDeviceType(model.type);
    request.name = model.name;
    request.icd9Code = model.icd9Code;
    request.code = model.code;
    request.bodyAreaCode = model.bodyAreaCode;
    request.nhfPoints = model.nhfPoints;
    request.withContrast = model.withContrast;
    request.withSpine = model.withSpine;
    request.withPrePostContrast = model.withPrePostContrast;
    request.defaultEtcInMinutes = model.defaultEtcInMinutes;

    return request;
  }

  mapFromApiModel(model: MedServiceDto): ProcedureData {
    return {
      type: model.type ?? 'CT',
      name: model.name ?? '',
      icd9Code: model.icd9Code ?? '',
      code: model.code ?? '',
      bodyAreaCode: model.bodyAreaCode ?? '',
      nhfPoints: model.nhfPoints ?? '',
      withContrast: model.withContrast ?? '',
      withSpine: model.withSpine ?? '',
      withPrePostContrast: model.withPrePostContrast ?? '',
      defaultEtcInMinutes: model.defaultEtcInMinutes ?? '',
    };
  }

  submit(): void {
    this.submitted.set(true);
    if (this.procedureForm().valid()) {
      this.isSubmitting.set(true);
      this.procedureService.update(this.mapToApiModel(this.procedureModel())).subscribe({
        next: (result) => {
          this.messageService.success('Procedura została zaktualizowana');
          this.router.navigate(['/procedure', result.id, 'details']);
        },
        error: (error) => {
          this.messageService.error('Wystąpił błąd podczas aktualizacji procedury');
          console.error(error);
          this.isSubmitting.set(false);
        },
        complete: () => {
          this.isSubmitting.set(false);
        },
      });
    }
  }

  filterOption = (input: string, option: NzSelectItemInterface): boolean => {
    return String(option.nzLabel).toLowerCase().includes(input.toLowerCase()) ?? false;
  };

  mapToDeviceType(value: string): DeviceTypes | undefined {
    return Object.values(DeviceTypes).includes(value as DeviceTypes)
      ? (value as DeviceTypes)
      : undefined;
  }
}
